import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Library, LogOut, MoreHorizontal, Settings, ShieldCheck } from 'lucide-react';
import { MOBILE_DESTINATIONS } from './destinations.js';

/**
 * Bottom navigation (<640px), spec §8.4: Chat · Missionen · Onboarding · Mehr.
 * Every target is at least 44×44 px (`--mobile-nav-height` plus the flexible
 * width of a quarter of the viewport).
 *
 * @param {object} props
 * @param {number|null} [props.dueCount]
 * @param {boolean} props.moreOpen
 * @param {() => void} props.onOpenMore
 */
export function MobileNavigation({ dueCount, moreOpen, onOpenMore }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <nav className="etch-app__mobile-nav" aria-label={t('nav.mainNavigation')}>
      <div className="etch-mobile-nav">
        {MOBILE_DESTINATIONS.map(({ id, to, labelKey, Icon, badge, isActive }) => {
          const showBadge = badge === 'due' && typeof dueCount === 'number' && dueCount > 0;
          return (
            <Link
              key={id}
              to={to}
              className="etch-mobile-nav__item"
              aria-current={isActive(pathname) ? 'page' : undefined}
            >
              <Icon aria-hidden="true" />
              <span>{t(labelKey)}</span>
              {showBadge && (
                <>
                  <span className="etch-mobile-nav__count" aria-hidden="true">
                    {dueCount}
                  </span>
                  <span className="etch-visually-hidden">{t('missions.dueCount', { count: dueCount })}</span>
                </>
              )}
            </Link>
          );
        })}

        <button type="button" className="etch-mobile-nav__item" aria-expanded={moreOpen} onClick={onOpenMore}>
          <MoreHorizontal aria-hidden="true" />
          <span>{t('nav.mobileMore')}</span>
        </button>
      </div>
    </nav>
  );
}

/**
 * Content of the "Mehr" sheet — the destinations that do not fit the bottom
 * bar. Rendered by `AppShell` inside a bottom `Drawer` (focus trap included).
 *
 * Deliberately does not reuse `.etch-nav-item`: below 1200px that class
 * collapses to a 44×44 icon square for the tablet rail, which is exactly wrong
 * inside a full-width sheet.
 *
 * @param {object} props
 * @param {boolean} props.isAdmin
 * @param {() => void} props.onNavigate
 * @param {() => void} props.onLogout
 */
export function MoreMenu({ isAdmin, onNavigate, onLogout }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <nav className="etch-shell-drawer-nav" aria-label={t('nav.mobileMore')}>
      <Link
        to="/knowledge"
        className="etch-shell-drawer-item"
        aria-current={pathname.startsWith('/knowledge') ? 'page' : undefined}
        onClick={onNavigate}
      >
        <Library size={18} aria-hidden="true" />
        {t('nav.knowledge')}
      </Link>
      <Link
        to="/settings"
        className="etch-shell-drawer-item"
        aria-current={pathname.startsWith('/settings') ? 'page' : undefined}
        onClick={onNavigate}
      >
        <Settings size={18} aria-hidden="true" />
        {t('nav.settings')}
      </Link>
      {isAdmin && (
        <Link
          to="/admin"
          className="etch-shell-drawer-item"
          aria-current={pathname.startsWith('/admin') ? 'page' : undefined}
          onClick={onNavigate}
        >
          <ShieldCheck size={18} aria-hidden="true" />
          {t('nav.admin')}
        </Link>
      )}
      <button type="button" className="etch-shell-drawer-item" data-tone="danger" onClick={onLogout}>
        <LogOut size={18} aria-hidden="true" />
        {t('auth.logout')}
      </button>
    </nav>
  );
}

export default MobileNavigation;
